/* eslint-disable no-console */
import {enableProdMode, ErrorHandler, importProvidersFrom, LOCALE_ID,} from '@angular/core';

import {LayoutModule} from '@angular/cdk/layout';
import {CommonModule, registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi,} from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import {ReactiveFormsModule} from '@angular/forms';
import {bootstrapApplication, BrowserModule, HammerModule,} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {provideRouter, RouteReuseStrategy} from '@angular/router';
import {TranslateCompiler, TranslateLoader, TranslateModule,} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import 'hammerjs';
import {SocketIoModule} from 'ngx-socket-io';
import {PhraseAppCompiler} from 'ngx-translate-phraseapp';
import {AppComponent} from './app/app.component';
import {APP_ROUTES} from './app/app.routes';
import {socketConfig} from './app/config/socket.config';
import {ACCOUNT_PROVIDERS} from '@core/entities/account/infrastructure/providers/account.provider';
import {AuditService} from '@core/entities/audit/audit.service';
import {BOOKING_PROVIDERS} from '@core/entities/booking/infrastructure/providers/booking.provider';
import {ORGANIZATION_PROVIDERS} from '@core/entities/organization/infrastructure/providers/organization.provider';
import {SERVICE_PROVIDERS} from '@core/entities/service/infrastructure/providers/service.provider';
import {
  SERVICE_PROVIDERS as SERVICE_FEATURES_NEW
} from './app/features/service/infrastructure/provider/service.provider';
import {WAITER_PROVIDERS} from '@core/entities/waiter/infrastructure/providers/waiter.provider';
import {SessionStorageRepository} from '@core/entities/session/infrastructure/implementations/session-storage';
import {SESSION_STORAGE_REPOSITORY} from '@core/entities/session/infrastructure/interfaces/session-storage.repository';
import {TicketEventService} from '@core/entities/ticket-events/ticket-event.service';
import {TICKET_PROVIDERS} from '@core/entities/ticket/infrastructure/providers/ticket.provider';
import {GlobalErrorHandler} from '@core/error-handlers/global-error-handler';
import {HeadersInterceptor} from '@core/interceptors/headers.interceptor';
import {HttpErrorInterceptor} from '@core/interceptors/http-error.interceptor';
import {Logger} from '@core/interfaces/Logger';
import {ProductsRouteReuseStrategy} from '@core/routers/products-route-reuse-strategy';
import {LoggerConsoleService} from '@core/services/logger/logger-console.service';
import {LoggerRollbarService} from '@core/services/logger/logger-rollbar.service';
import {SocketConnectionHandlerService} from '@core/services/socket/socket-connection-handler.service';
import {SocketRoomHandlerService} from '@core/services/socket/socket-room-handler.service';
import {AppContextService} from '@shared/services/app-context/app-context.service';
import {LayoutService} from '@shared/services/layout/layout.service';
import {environment} from '@env/environment';
import {ENVIRONMENT_NAME} from '@env/environment-name.enum';
import './polyfills/zxing-patch';
import {PAYMENT_LEGACY_PROVIDERS,} from "@core/entities/payment/infrastructure/providers/payment.provider";
import {ENTRY_PROVIDERS} from "@core/entities/entry/infrastructure/providers/entry.provider";
import {PRODUCT_FOLDER_PROVIDERS} from '@core/entities/product-folder/infrastructure/providers/product-folder.provider';
import {WALLET_PROVIDERS} from "@features/wallet/infrastructure/provider/wallet.provider";
import {SHARED_PROVIDERS} from "@shared/infrastructure/providers/shared.provider";
import {PAYMENT_PROVIDERS} from "@features/payment/infrastructure/provider/payment.provider";
import {SALES_PROVIDERS} from "@features/sales/infrastructure/provider/sales.provider";
import {CONSUMPTION_PROVIDERS} from '@core/entities/consumption/infrastructure/provider/consumption.provider';
import {PLATFORM_PROVIDERS} from '@platform/platform.providers';
import {PERMISSION_PROVIDERS} from '@features/permission/infrastructure/provider/permission.provider';

registerLocaleData(localeEs);

const inContextPhraseCompilerObj = {
  compiler: {
    provide: TranslateCompiler,
    useClass: PhraseAppCompiler,
  },
};

const inContextPhraseCompiler = environment.production
  ? {}
  : inContextPhraseCompilerObj;

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(APP_ROUTES),
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      BrowserAnimationsModule,
      ReactiveFormsModule,
      SocketIoModule.forRoot(socketConfig),
      LayoutModule,
      HammerModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
        ...inContextPhraseCompiler,
      })
    ),
    SocketConnectionHandlerService,
    SocketRoomHandlerService,
    AuditService,
    TicketEventService,
    AppContextService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeadersInterceptor,
      multi: true,
    },
    {provide: RouteReuseStrategy, useClass: ProductsRouteReuseStrategy},
    {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},
    {provide: LOCALE_ID, useValue: 'es-ES'},
    {provide: SESSION_STORAGE_REPOSITORY, useClass: SessionStorageRepository},
    ...BOOKING_PROVIDERS,
    ...ACCOUNT_PROVIDERS,
    ...WAITER_PROVIDERS,
    ...SHARED_PROVIDERS,
    ...ORGANIZATION_PROVIDERS,
    ...SERVICE_PROVIDERS,
    ...TICKET_PROVIDERS,
    ...PAYMENT_LEGACY_PROVIDERS,
    ...ENTRY_PROVIDERS,
    ...PRODUCT_FOLDER_PROVIDERS,
    ...WALLET_PROVIDERS,
    ...PAYMENT_PROVIDERS,
    ...SALES_PROVIDERS,
    ...PLATFORM_PROVIDERS,
    ...CONSUMPTION_PROVIDERS,
    ...PERMISSION_PROVIDERS,
    ...SERVICE_FEATURES_NEW,
    ...PLATFORM_PROVIDERS,
    {
      provide: Logger,
      useExisting:
        environment.production || environment.name === ENVIRONMENT_NAME.alpha
          ? LoggerRollbarService
          : LoggerConsoleService,
    },
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    LayoutService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
  .catch((error) => {
    console.debug(`[PWA 🌎] [Error during bootstrap] ${error}`);
  });

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}
